import { Component } from 'react'
import { T } from '@/lib/theme'

/// Catches render-path exceptions so a single component failure
/// doesn't nuke the entire app into a dark-blue React-error screen.
/// Wraps each page body. Users get a localised "this section failed"
/// card with a reload, and the error bubbles to the console for devs.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Surface the crash to the dev console. On a real deploy this
    // would also fire to an ops channel (Sentry/etc.).
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    if (!this.state.error) return this.props.children

    const msg = this.state.error?.message || 'Unknown render error'

    return (
      <div style={{
        maxWidth: 720,
        margin: '80px auto',
        padding: 24,
        borderRadius: T.r.lg,
        background: 'rgba(10, 14, 28, 0.55)',
        backdropFilter: 'blur(3px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(3px) saturate(1.4)',
        border: `1px solid ${T.negative}55`,
      }}>
        <div style={{
          color: T.negative,
          fontFamily: T.fontM,
          fontSize: T.fs.ui,
          fontWeight: 700,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}>This section failed to render</div>
        <p style={{
          color: T.textMuted,
          fontFamily: T.fontM,
          fontSize: T.fs.body,
          marginTop: 12,
          lineHeight: 1.5,
        }}>
          A component threw while rendering. The rest of the app is still
          working; reload to recover this section, or report the message
          below to the team.
        </p>
        <pre style={{
          marginTop: 14,
          padding: '10px 12px',
          borderRadius: T.r.md,
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${T.border}`,
          color: T.text,
          fontFamily: T.fontN,
          fontSize: T.fs.caption,
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
        }}>{msg}</pre>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 14,
            padding: '10px 16px',
            borderRadius: T.r.md,
            border: `1px solid ${T.border}`,
            background: T.glassButton,
            color: T.text,
            fontFamily: T.fontM,
            fontWeight: 700,
            fontSize: T.fs.ui,
            cursor: 'pointer',
          }}
        >Reload</button>
      </div>
    )
  }
}
